import { useState, useEffect, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";

// ── Employee lookup panel ──────────────────────────────────────────────────
function EmployeeLookupPanel({ onSelect }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(null);

  const handleSearch = async () => {
    if (!firstName.trim() && !lastName.trim()) {
      setError("Enter at least a first or last name.");
      return;
    }
    setLoading(true);
    setError("");
    setResults(null);
    setSelected(null);
    try {
      const res = await fetch(`${API_BASE}/employee-search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
      setResults(data.results || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  const handleSelect = (row) => {
    setSelected(row.employeeNumber);
    onSelect(row.employeeNumber);
  };

  return (
    <div className="as-lookup-panel">
      <div className="as-lookup-title">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        Search by name — click a row to populate the Actor/EEID field
      </div>
      <div className="as-lookup-inputs">
        <div className="as-lookup-field">
          <label className="control-label">First Name</label>
          <input
            autoFocus
            className="cug-input"
            placeholder="e.g. Dhairya"
            value={firstName}
            onChange={(e) => {
              setFirstName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </div>
        <div className="as-lookup-field">
          <label className="control-label">Last Name</label>
          <input
            className="cug-input"
            placeholder="e.g. Shah"
            value={lastName}
            onChange={(e) => {
              setLastName(e.target.value);
              setError("");
            }}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
        </div>
        <button
          className="compare-btn"
          onClick={handleSearch}
          disabled={loading || (!firstName.trim() && !lastName.trim())}
          style={{ alignSelf: "flex-end" }}
        >
          {loading ? (
            <>
              <span className="spinner" /> Searching…
            </>
          ) : (
            <>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              Search
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="as-error-bar" style={{ margin: "10px 0 0" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {results !== null &&
        (results.length === 0 ? (
          <div className="as-lookup-empty">No employees found. Try different names.</div>
        ) : (
          <div className="as-lookup-table-wrap">
            <div className="as-lookup-thead">
              <div className="as-lth">Employee ID</div>
              <div className="as-lth">First Name</div>
              <div className="as-lth">Last Name</div>
              <div className="as-lth">Email</div>
            </div>
            <div className="as-lookup-tbody">
              {results.map((r, i) => (
                <div
                  key={i}
                  className={`as-lookup-tr ${selected === r.employeeNumber ? "as-lookup-tr-selected" : ""}`}
                  onClick={() => handleSelect(r)}
                >
                  <div className="as-ltd">
                    <span className="as-emp-id-badge">{r.employeeNumber || "—"}</span>
                  </div>
                  <div className="as-ltd">{r.firstName || "—"}</div>
                  <div className="as-ltd">{r.lastName || "—"}</div>
                  <div className="as-ltd as-ltd-muted">{r.email || "—"}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function ActorOrgUnitAssignment() {
  const [actor, setActor] = useState("");
  const [pastedOrgUnits, setPastedOrgUnits] = useState("");
  const [allOrgUnits, setAllOrgUnits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [showLookup, setShowLookup] = useState(false);
  const actorInputRef = useRef(null);

  // Fetch org units from S3 on mount
  useEffect(() => {
    fetchOrgUnits();
  }, []);

  const fetchOrgUnits = async () => {
    try {
      const response = await fetch(`${API_BASE}/myhr/org-units`);
      const data = await response.json();
      setAllOrgUnits(data.orgUnits || []);
    } catch (error) {
      console.error("Error fetching org units:", error);
    }
  };

  // Validate pasted org units against S3
  const validateOrgUnits = (text) => {
    if (!text.trim()) return { valid: [], invalid: [], total: 0 };
    
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    const validated = lines.map((line) => {
      const exists = allOrgUnits.some((unit) => unit.id === line || unit.code === line);
      return { value: line, valid: exists };
    });

    return {
      valid: validated.filter((v) => v.valid),
      invalid: validated.filter((v) => !v.valid),
      total: validated.length,
      items: validated,
    };
  };

  const validation = validateOrgUnits(pastedOrgUnits);

  const handleSubmit = async () => {
    if (!actor.trim()) {
      setError("Please enter an Actor/EEID");
      return;
    }

    const orgUnits = pastedOrgUnits
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    if (orgUnits.length === 0) {
      setError("Please provide at least one organizational unit");
      return;
    }

    setLoading(true);
    setResults(null);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/myhr/actor-org-units`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          actor: actor.trim(),
          orgUnits: orgUnits,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResults(data);
        // Clear form on success
        setPastedOrgUnits("");
      } else {
        setError(data.error || "Failed to process org units");
      }
    } catch (err) {
      console.error("Error submitting org units:", err);
      setError("Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setActor("");
    setPastedOrgUnits("");
    setResults(null);
    setError("");
    setShowLookup(false);
    actorInputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
      handleSubmit();
    }
  };

  const handleLookupSelect = (empId) => {
    setActor(empId);
    setShowLookup(false);
    setError("");
    actorInputRef.current?.focus();
  };

  return (
    <div className="as-root">
      {/* ── Header ── */}
      <div className="as-header">
        <div className="as-header-inner">
          <div className="logo-mark">O</div>
          <div>
            <div className="as-title">Assign Organizational Units</div>
            <div className="as-subtitle">Assign multiple org units to an actor in myHR (Lawson)</div>
          </div>
        </div>
      </div>

      {/* ── Input Section ── */}
      <div className="as-search-bar">
        <div className="as-search-inner">
          {/* Actor Input */}
          <div className="as-search-group">
            <div className="as-search-row">
              <label className="control-label" style={{ marginBottom: 0 }}>Actor / EEID</label>
              <input
                ref={actorInputRef}
                className="cug-input as-id-input"
                placeholder="Enter employee ID (e.g., 12345)"
                value={actor}
                onChange={(e) => {
                  setActor(e.target.value);
                  setError("");
                }}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              <button
                className={`as-lookup-toggle ${showLookup ? "as-lookup-toggle-active" : ""}`}
                onClick={() => setShowLookup(!showLookup)}
                disabled={loading}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                </svg>
                {showLookup ? "Hide Name Search" : "Don't know the Employee ID?"}
                <svg
                  width="11"
                  height="11"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  style={{ transform: showLookup ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
            </div>
            <small style={{ color: "var(--text-muted)", display: "block", marginTop: "5px" }}>
              HROrganization: <strong style={{ color: "var(--green)" }}>KOCH</strong> (applied to all)
            </small>
          </div>
        </div>

        {/* ── Employee Lookup Panel ── */}
        {showLookup && (
          <div className="as-lookup-wrap">
            <EmployeeLookupPanel onSelect={handleLookupSelect} />
          </div>
        )}
      </div>

      {/* ── Org Units Section ── */}
      <div className="as-search-bar">
        <div className="as-search-inner">
          {/* Org Units Textarea */}
          <div className="as-search-group">
            <label className="control-label">Organizational Units (one per line)</label>
            <textarea
              className="cug-input"
              rows={10}
              placeholder="Paste org unit codes here, one per line&#10;IT-DEPT&#10;HR-FINANCE&#10;SUPPLY-CHAIN"
              value={pastedOrgUnits}
              onChange={(e) => {
                setPastedOrgUnits(e.target.value);
                setError("");
              }}
              disabled={loading}
              style={{
                fontFamily: "var(--font-mono)",
                resize: "vertical",
              }}
            />

            {/* Validation Display */}
            {validation.total > 0 && (
              <div
                style={{
                  marginTop: "10px",
                  padding: "12px",
                  backgroundColor: "var(--bg-surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  fontSize: "13px",
                }}
              >
                <div style={{ display: "flex", gap: "15px", marginBottom: "8px" }}>
                  <span style={{ color: "var(--green)" }}>
                    ✓ Valid: {validation.valid.length}
                  </span>
                  {validation.invalid.length > 0 && (
                    <span style={{ color: "var(--red)" }}>
                      ✗ Invalid: {validation.invalid.length}
                    </span>
                  )}
                </div>
                {validation.invalid.length > 0 && (
                  <details style={{ marginTop: "8px" }}>
                    <summary style={{ cursor: "pointer", color: "var(--text-muted)" }}>
                      Show invalid org units
                    </summary>
                    <ul style={{ margin: "5px 0", paddingLeft: "20px", color: "var(--red)" }}>
                      {validation.invalid.map((item, idx) => (
                        <li key={idx}>{item.value}</li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div style={{ display: "flex", gap: "10px", marginTop: "15px" }}>
            <button className="compare-btn" onClick={handleSubmit} disabled={loading || !actor.trim() || validation.total === 0}>
              {loading ? (
                <>
                  <span className="spinner" /> Processing…
                </>
              ) : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Assign Org Units
                </>
              )}
            </button>
            <button className="cug-reset-btn" onClick={handleReset} disabled={loading}>
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* ── Error Display ── */}
      {error && (
        <div className="as-error-bar" style={{ margin: "20px auto", maxWidth: "1200px" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {error}
        </div>
      )}

      {/* ── Results Section ── */}
      {results && (
        <div className="as-results-wrap">
          <div className="as-results-inner">
            {/* Summary Line */}
            <div className="as-count-text">
              {results.summary.total} org unit assignment{results.summary.total !== 1 ? "s" : ""} processed
              {results.summary.created > 0 && (
                <span style={{ color: "var(--green)", marginLeft: "12px" }}>
                  ● {results.summary.created} created
                </span>
              )}
              {results.summary.skipped > 0 && (
                <span style={{ color: "var(--amber)", marginLeft: "12px" }}>
                  ● {results.summary.skipped} skipped
                </span>
              )}
              {results.summary.failed > 0 && (
                <span style={{ color: "var(--red)", marginLeft: "12px" }}>
                  ● {results.summary.failed} failed
                </span>
              )}
            </div>

            {/* ServiceNow Incident */}
            {results.serviceNowIncident && (
              <div className="as-count-text" style={{ fontSize: "13px", color: "var(--text-muted)", marginTop: "8px" }}>
                {results.serviceNowIncident.created ? (
                  <>
                    ServiceNow Task: <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>{results.serviceNowIncident.number}</span>
                  </>
                ) : (
                  <span style={{ color: "var(--red)" }}>
                    ServiceNow task was not created: {results.serviceNowIncident.error || "Unknown error"}
                  </span>
                )}
              </div>
            )}

            {/* Detailed Results Table */}
            <div className="as-table-wrap">
              <div className="as-table">
                <div className="as-thead">
                  <div className="as-th" style={{ flex: "0 0 150px" }}>Org Unit</div>
                  <div className="as-th" style={{ flex: "0 0 120px" }}>Actor</div>
                  <div className="as-th" style={{ flex: "0 0 120px" }}>Status</div>
                  <div className="as-th" style={{ flex: "1 1 auto" }}>Message</div>
                </div>
                <div className="as-tbody">
                  {results.results.map((result, idx) => (
                    <div key={idx} className="as-tr">
                      <div className="as-td" style={{ flex: "0 0 150px" }}>
                        <span className="as-source-badge">{result.orgUnit}</span>
                      </div>
                      <div className="as-td" style={{ flex: "0 0 120px" }}>
                        <span className="as-emp-id-badge">{result.actor}</span>
                      </div>
                      <div className="as-td" style={{ flex: "0 0 120px" }}>
                        <span
                          className={`as-grant-badge ${
                            result.status === "created"
                              ? "as-grant-requested"
                              : result.status === "skipped"
                              ? "as-grant-birthright"
                              : ""
                          }`}
                          style={{
                            backgroundColor:
                              result.status === "created"
                                ? "var(--green-dim)"
                                : result.status === "skipped"
                                ? "var(--amber-dim)"
                                : "var(--red-dim)",
                            color:
                              result.status === "created"
                                ? "var(--green)"
                                : result.status === "skipped"
                                ? "var(--amber)"
                                : "var(--red)",
                            borderColor:
                              result.status === "created"
                                ? "var(--green)"
                                : result.status === "skipped"
                                ? "var(--amber)"
                                : "var(--red)",
                          }}
                        >
                          {result.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="as-td as-desc-text" style={{ flex: "1 1 auto" }}>
                        {result.message}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="as-table-footer">
                Showing {results.results.length} org unit{results.results.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
